import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { EMPTY, forkJoin, map, Observable, switchMap, take, tap } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { CivilInfoReferralState } from '../states/civil-case-referral.state';
import { ReferralInfoApiService } from '../../service/referral-info-api.service';
import { TemplateMapService } from 'src/app/shared/services/template-map.service';
import { SnackbarService } from 'src/app/core/services/snackbar.service';
import { PageTitleService } from 'src/app/core/services/page-title.service';
import { CivilCaseModel } from 'src/app/shared/models/civil-case.model';
import { CaseConfigTab } from 'src/app/shared/models/case-config-tab.model';

@Injectable({ providedIn: 'root' })
export class CivilCaseReferralResolver implements Resolve<CivilCaseModel | null> {
  isDataLoaded: boolean = false;
  constructor(
    private service: ReferralInfoApiService,
    private pageTitleService: PageTitleService,
    private templateMapService: TemplateMapService,
    private titleService: Title,
    private state: CivilInfoReferralState,
    private router: Router,
    private snackbarService: SnackbarService,
    private activatedRoute: ActivatedRoute
  ) { }

  resolve(route: ActivatedRouteSnapshot): Observable<CivilCaseModel | null> {
    const sequenceNumber = route.paramMap.get('sequeceNumber');
    const casePageTypeId = route.queryParamMap.get('CaseTypeId') ?? '';
    const isCriminal = route.queryParamMap.get('isDefaultFlow') == 'false';
    if (!sequenceNumber) {
      return EMPTY;
    }
    return this.state.checkReferralStatus(sequenceNumber).pipe(
      switchMap((status: string) => {
        if (status === 'Referred') {
          this.snackbarService.showError('This case has been referred.');
          this.router.navigate(['/agency-home']);
          return EMPTY;
        }

        this.state.setParameters(sequenceNumber);
        this.state.loadData();

        return forkJoin({
          config: this.service.getCivilCaseReferralConfigs(casePageTypeId, sequenceNumber),
          userDefinedFields: this.service.getUserDefineCaseConfigControlsDataReferral(sequenceNumber),
          data: this.service.getCaseConfigControlsDataReferral(sequenceNumber),
          tabsMeta: this.service.getReferralInfoTabDetails(Number(sequenceNumber))
        }).pipe(
          tap(result => {
            // this.setPageTitle(result.config.templateTitle);
            this.titleService.setTitle(result.config.templateTitle.split('-')[0]);
          }),
          tap(result => {
            if (result.data.isShowBond) {
              result.config.tabs.push(<CaseConfigTab>{ identifier: 'BOND' });
            }
          }),
          map(result => {
            const stateCalls$: Observable<boolean>[] = [];
            forkJoin(stateCalls$)
              .pipe(
                take(1)
                // finalize(() => this.layoutState.setLoadingStatus(false))
              )
              .subscribe((res: any) => {
                this.isDataLoaded = true;
              });

            return <CivilCaseModel>{
              id: sequenceNumber,
              templateId: result.config.templateId,
              templateTitle: result.config.templateTitle,
              isCivil: result.config.isCivil,
              sections: result.config.sections.map(section => ({
                ...section,
                width: section.id.includes('P') ? `col-${section.width}` : `${section.width}fr`,
                fields: result.config.fields
                  .filter(field => field.sectionId === section.id)
                  .map(field => this.templateMapService.mapField(sequenceNumber, field, result.data, result.userDefinedFields, result.config.isCivil))
                  .concat(
                    result.config.userDefinedFields
                      .filter(field => field.sectionId === section.id)
                      .map(field =>
                        this.templateMapService.mapUserDefineField(field, result.userDefinedFields)
                      )
                  )
                  .sort((field1, field2) => field1.order - field2.order),
              })),
              tabs: result.config.tabs.map(t => this.templateMapService.mapTab(t)).filter(t => !!t),
              casePageTypeId: casePageTypeId,
              docCategory: result.data.docCategory,
              tabsMeta: result.tabsMeta
            };
          })
        );
      })
    );
  }

  private setPageTitle(header: string) {
    const titleParts = this.activatedRoute.snapshot.data[
      "pageName"
    ] as string[];

    titleParts[0] = header;
    if (titleParts?.length) {
      this.pageTitleService.setTitle(titleParts);
    }
  }
}
